import React from 'react';

/**
 * ヒーロー用メインビジュアル。
 * 「AIは手元のPCの中で動き、データは外に出ない」という製品の核を一枚で伝える。
 * 左：切断されたクラウド接続 / 中央：アプリ画面 / 右下：ローカル保存の盾
 */
const LocalAiArt: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 600 460"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    role="img"
    aria-label="AIが手元のPC内で動作し、会計データが外部に送信されないことを示す図"
  >
    <defs>
      <linearGradient id="scr" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#FFFFFF" />
        <stop offset="100%" stopColor="#F6F8FB" />
      </linearGradient>
      <linearGradient id="bar" x1="0" y1="1" x2="0" y2="0">
        <stop offset="0%" stopColor="#2F5B85" />
        <stop offset="100%" stopColor="#5D82A8" />
      </linearGradient>
      <filter id="soft" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="10" stdDeviation="16" floodColor="#0A2540" floodOpacity="0.13" />
      </filter>
    </defs>

    {/* 背景の柔らかい円 */}
    <circle cx="300" cy="215" r="196" fill="#F2F6FA" />
    <circle cx="300" cy="215" r="196" stroke="#E3EBF3" strokeWidth="1.5" />

    {/* 左上：クラウドと「接続しない」表現 */}
    <g opacity="0.85">
      <path
        d="M74 92c-11 0-20 9-20 20s9 20 20 20h58c13 0 24-11 24-24s-11-24-24-24c-3 0-6 .6-9 1.7C119 74 108 66 95 66c-16 0-29 13-29 29 0 .9 0 1.8.2 2.7A20 20 0 0 0 74 92Z"
        fill="#FFFFFF"
        stroke="#C4D4E5"
        strokeWidth="2.5"
      />
      {/* 切断された通信線 */}
      <path d="M124 140v34" stroke="#C4D4E5" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="7 7" />
      <path d="M124 206v26" stroke="#C4D4E5" strokeWidth="2.5" strokeLinecap="round" strokeDasharray="7 7" />
      {/* 遮断マーク */}
      <circle cx="124" cy="190" r="16" fill="#FEF4EE" stroke="#F26B21" strokeWidth="2.5" />
      <path d="m118 184 12 12M130 184l-12 12" stroke="#F26B21" strokeWidth="2.5" strokeLinecap="round" />
    </g>

    {/* 中央：アプリ画面 */}
    <g filter="url(#soft)">
      <rect x="150" y="104" width="352" height="238" rx="14" fill="url(#scr)" stroke="#D8E2ED" strokeWidth="2" />
      {/* タイトルバー */}
      <path d="M150 118a14 14 0 0 1 14-14h324a14 14 0 0 1 14 14v22H150v-22Z" fill="#0A2540" />
      <circle cx="171" cy="122" r="4" fill="#4A6480" />
      <circle cx="186" cy="122" r="4" fill="#4A6480" />
      <circle cx="201" cy="122" r="4" fill="#4A6480" />
      <rect x="224" y="115" width="128" height="14" rx="7" fill="#1B3B5C" />

      {/* 左サイドバー */}
      <rect x="150" y="140" width="74" height="202" fill="#F6F8FB" />
      <rect x="164" y="158" width="46" height="7" rx="3.5" fill="#C4D4E5" />
      <rect x="164" y="176" width="38" height="7" rx="3.5" fill="#DDE6EF" />
      <rect x="164" y="194" width="42" height="7" rx="3.5" fill="#DDE6EF" />
      <rect x="164" y="212" width="34" height="7" rx="3.5" fill="#DDE6EF" />
      <rect x="158" y="150" width="3" height="22" rx="1.5" fill="#F26B21" />

      {/* 棒グラフ */}
      <rect x="248" y="266" width="26" height="42" rx="4" fill="url(#bar)" />
      <rect x="286" y="240" width="26" height="68" rx="4" fill="url(#bar)" />
      <rect x="324" y="252" width="26" height="56" rx="4" fill="url(#bar)" />
      <rect x="362" y="212" width="26" height="96" rx="4" fill="#F26B21" />
      <rect x="400" y="232" width="26" height="76" rx="4" fill="url(#bar)" />
      <path d="M240 308h202" stroke="#D8E2ED" strokeWidth="2" strokeLinecap="round" />

      {/* 折れ線（推移） */}
      <path
        d="m248 232 38-22 38 14 38-32 38 18"
        stroke="#0F9D58"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      <circle cx="400" cy="210" r="4.5" fill="#FFFFFF" stroke="#0F9D58" strokeWidth="2.5" />

      {/* 見出し行 */}
      <rect x="240" y="160" width="92" height="9" rx="4.5" fill="#0A2540" />
      <rect x="240" y="180" width="150" height="7" rx="3.5" fill="#C4D4E5" />

      {/* 右上のAIバッジ */}
      <rect x="410" y="154" width="72" height="26" rx="13" fill="#FEF4EE" stroke="#F7A26E" strokeWidth="1.5" />
      <path
        d="m424 161 2.2 5.3 5.3 2.2-5.3 2.2-2.2 5.3-2.2-5.3-5.3-2.2 5.3-2.2 2.2-5.3Z"
        fill="#F26B21"
      />
      <rect x="436" y="163" width="34" height="8" rx="4" fill="#F4823F" />
    </g>

    {/* AIチャットの吹き出し */}
    <g filter="url(#soft)">
      <path
        d="M330 344h150a14 14 0 0 1 14 14v44a14 14 0 0 1-14 14H366l-20 20v-20h-16a14 14 0 0 1-14-14v-44a14 14 0 0 1 14-14Z"
        fill="#FFFFFF"
        stroke="#D8E2ED"
        strokeWidth="2"
      />
      <rect x="348" y="362" width="112" height="8" rx="4" fill="#C4D4E5" />
      <rect x="348" y="380" width="76" height="8" rx="4" fill="#DDE6EF" />
    </g>

    {/* 右下：ローカル保存の盾 */}
    <g filter="url(#soft)">
      <path
        d="M170 306c0-6 4-11 10-13l38-13c3-1 6-1 9 0l38 13c6 2 10 7 10 13v34c0 30-20 56-48 65-2 1-5 1-7 0-28-9-50-35-50-65v-34Z"
        fill="#FFFFFF"
        stroke="#0A2540"
        strokeWidth="2.5"
      />
      <path
        d="M197 355a12 12 0 0 1 12-12h18a12 12 0 0 1 12 12v20a6 6 0 0 1-6 6h-30a6 6 0 0 1-6-6v-20Z"
        fill="#0A2540"
      />
      <path
        d="M206 343v-9a12 12 0 0 1 24 0v9"
        stroke="#0A2540"
        strokeWidth="4"
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="218" cy="364" r="4.5" fill="#F26B21" />
    </g>
  </svg>
);

export default LocalAiArt;
